"use client";
import { useTranslations } from "@zapier/i18n";

import { Text } from "@zapier/design-system";
import { useMemo, useState, useEffect } from "react";
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from "react-resizable-panels";
import styles from "./AssetLayout.module.css";
import foldersStyles from "./FoldersLayout.module.css";
import { FolderTree } from "../../folders/FolderTree/V2";
import { TrashLink } from "./TrashLink";
import OtherMembersPersonalFoldersAccordion from "./OtherMembersPersonalFoldersAccordion";
import {
  FolderTreeItem,
  useFolderTree,
  useFolderAncestors,
} from "@zapier/client";
import { useParams, usePathname } from "next/navigation";
import {
  FolderTypeQueryState,
  DEFAULT_FOLDER_ID,
  parseFolderId,
} from "@zapier/utils";

import { useEmitAssetSidebarResizeEvent } from "../../../events/useEmitAssetSidebarResizeEvent";
import { useRouterParams } from "../../../hooks/useRouterParams";

function collectFolderIds(folders: FolderTreeItem[]): Set<string> {
  const ids = new Set<string>();
  for (const folder of folders) {
    ids.add(String(folder.id));
    if (folder.children?.length) {
      for (const id of collectFolderIds(folder.children as FolderTreeItem[])) {
        ids.add(id);
      }
    }
  }
  return ids;
}

const FoldersLayout = ({ children }: { children: React.ReactNode }) => {
  const t = useTranslations("asset-layout");
  const { emit: emitResizeEvent } = useEmitAssetSidebarResizeEvent();
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: "folders-assets-layout",
    storage: localStorage,
  });

  const { folders: myFolders } = useFolderTree({
    folderType: FolderTypeQueryState.OwnedByMe,
    includeDefaultFolder: true,
    order: "title",
  });

  const { folders: sharedFolders } = useFolderTree({
    folderType: FolderTypeQueryState.SharedWithMe,
    order: "title",
  });

  useEffect(() => {
    localStorage.setItem("UniversalSidebar:isSidebarForcedClosed", "true");
  }, []);

  const parameters = useParams();
  const pathname = usePathname();
  const { memberId } = useRouterParams();

  const currentFolderId = useMemo(() => {
    if (parameters?.folder) {
      return String(parseFolderId(parameters.folder as string));
    }
    if (pathname === "/app/assets/folders") {
      return "root-my";
    }
    return;
  }, [parameters?.folder, pathname]);

  const urlSelectedFolderId = useMemo(() => {
    // Accordion root nodes are section headers, not navigable destinations
    if (currentFolderId === "root-my" || currentFolderId === "root-shared") {
      return;
    }
    if (currentFolderId === DEFAULT_FOLDER_ID && memberId) {
      return;
    }
    return currentFolderId;
  }, [currentFolderId, memberId]);

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(
    urlSelectedFolderId,
  );

  useEffect(() => {
    setSelectedFolderId(urlSelectedFolderId);
  }, [urlSelectedFolderId]);

  const { ancestors, isAncestorsLoading } = useFolderAncestors({
    folderId: currentFolderId,
  });

  const sharedFolderIds = useMemo(
    () => collectFolderIds(sharedFolders),
    [sharedFolders],
  );

  const defaultExpandedFolders = useMemo(() => {
    // Always start with "My folders" expanded
    const ids = new Set(["root-my"]);
    for (const ancestor of ancestors ?? []) {
      ids.add(String(ancestor.id));
    }
    // Only expand "Shared with me" root when the current folder is actually in the shared tree
    if (
      currentFolderId &&
      currentFolderId !== "root-my" &&
      currentFolderId !== "root-shared" &&
      !isAncestorsLoading &&
      sharedFolderIds.has(currentFolderId)
    ) {
      ids.add("root-shared");
    }
    return [...ids];
  }, [ancestors, currentFolderId, isAncestorsLoading, sharedFolderIds]);

  return (
    <>
      <Group
        className={styles.layout}
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel defaultSize={"15%"} minSize={"10%"} maxSize={"30%"} id="left">
          <div className={styles.sidebar}>
            <div className={styles.header}>
              <Text tag="h2" type="SectionHeader">
                {t("headings.folders")}
              </Text>
            </div>

            <div className={foldersStyles.folderSections}>
              <div className={foldersStyles.folderSection}>
                <FolderTree
                  isNavigation
                  rootAsAccordion
                  folders={myFolders}
                  rootName={t("headings.my-folders")}
                  rootId="root-my"
                  onFolderClick={(folder) => {
                    setSelectedFolderId(String(folder.id));
                  }}
                  selectedFolderId={selectedFolderId}
                  defaultExpandedFolders={defaultExpandedFolders}
                  aria-label={t("headings.my-folders")}
                  className={foldersStyles.myFolderTree}
                />
              </div>

              <div className={foldersStyles.folderSection}>
                <FolderTree
                  isNavigation
                  rootAsAccordion
                  folders={sharedFolders}
                  rootName={t("headings.shared-with-me")}
                  rootId="root-shared"
                  onFolderClick={(folder) => {
                    setSelectedFolderId(String(folder.id));
                  }}
                  selectedFolderId={selectedFolderId}
                  defaultExpandedFolders={defaultExpandedFolders}
                  aria-label={t("headings.shared-with-me")}
                  className={foldersStyles.sharedFolderTree}
                />
              </div>

              <div className={foldersStyles.folderSection}>
                <OtherMembersPersonalFoldersAccordion />
              </div>
            </div>
            <TrashLink />
          </div>
        </Panel>

        <Separator
          className={styles.resizeHandle}
          aria-label={t("resize-handle.label")}
          onDragEnd={(event) => emitResizeEvent(event.clientX)}
        />

        <Panel className={styles.contentPanel} id="right">
          <div className={styles.content}>{children}</div>
        </Panel>
      </Group>
    </>
  );
};

export default FoldersLayout;
